import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimations } from '../ScrollAnimations';
import './HowItWorks.css';
import './Home.css';

function WhyItWorks() {
  useScrollAnimations();
  
  return (
    <div className="how-it-works-page">
      <section className="hiw-hero" style={{
        background: 'linear-gradient(135deg, #FF6B35 0%, #e55a2b 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Option 3: Pattern of small logos */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.08,
          backgroundImage: 'url(/fit-logo.jpg)',
          backgroundSize: '150px 150px',
          backgroundRepeat: 'repeat',
          zIndex: 0
        }}></div>
        <div className="container" style={{position: 'relative', zIndex: 1}}>
          <div style={{backgroundColor: '#000', display: 'inline-block', padding: '20px 40px', borderRadius: '8px'}}>
            <h1 style={{color: 'white', margin: 0}}>Why <span className="fit-inline"><span className="f-orange" style={{color: '#FF6B35'}}>F</span>-IT</span> Score Works</h1>
          </div>
          <p className="hero-tagline-mobile">Born from lived experience, built on psychology, driven by passion</p>
          <p className="lead">The psychology and methodology behind the system</p>
        </div>
      </section>

      {/* Psychology Section */}
      <section className="psychology-section" style={{backgroundImage: `url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZXVyYWwlMjBuZXR3b3JrfGVufDB8fHx8MTc2NTI4NjIwNnww&ixlib=rb-4.1.0&q=85&sat=-100)`}}>
        {/* Pulsing circles */}
        <div className="pulse-circle pulse-circle-1"></div>
        <div className="pulse-circle pulse-circle-2"></div>
        <div className="pulse-circle pulse-circle-3"></div>
        <div className="pulse-circle pulse-circle-4"></div>
        <div className="pulse-circle pulse-circle-5"></div>
        <div className="pulse-circle pulse-circle-6"></div>
        <div className="pulse-circle pulse-circle-7"></div>
        <div className="pulse-circle pulse-circle-8"></div>
        <div className="pulse-circle pulse-circle-9"></div>
        <div className="pulse-circle pulse-circle-10"></div>
        <div className="container">
          <h2 className="section-title fade-in-up">The Psychology</h2>
          <div className="psychology-content">
            <p>Companies spend fortunes on safety campaigns and wellbeing initiatives, each with their own buzzwords and slogans. These campaigns often lose focus, run out of impact, and fade over time - only to be replaced by the next reinvention of the&nbsp;wheel.</p>
            
            <p>People are wary of corporate buzzwords. They've heard them all&nbsp;before.</p>
            
            <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> is different.</p>
            
            <div className="key-insight fade-in-left">
              <h3>The Power of Authenticity</h3>
              <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> works because it's the word people <strong>already use in their heads</strong>. When faced with a difficult decision, a risk, or pressure - that's the phrase that runs through their mind: <em>"F**k it."</em></p>
              
              <p>This isn't a manufactured slogan. It's raw human emotion. It's real.</p>
              
              <p>Professor Tim Marsh recognised the psychological power of this moment - that split second when someone makes a choice that could change everything. By naming it, understanding it, and measuring it, we can finally address what's really happening in people's minds at critical moments.</p>
            </div>
            
            <div className="key-insight fade-in-right">
              <h3>Beyond Buzzwords</h3>
              <p>Unlike campaigns that fade, <span className="fit-inline"><span className="f-orange">F</span>-IT</span> has permanent relevance because it connects to genuine human experience. It's not corporate speak - it's the language people actually use when they're under pressure, overwhelmed, or struggling.</p>
              
              <p>That's why <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score endures where other initiatives fail. It's honest, it's memorable, and it&nbsp;matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* F-IT vs F-YOU System */}
      <section className="system-overview">
        <div className="container">
          <h2 className="section-title section-title-nowrap">The Complete System: <span className="fit-inline"><span className="f-orange">F</span>&#8209;IT</span> & <span className="fit-inline"><span className="f-blue">F</span>&#8209;YOU</span></h2>
          
          {/* Mobile logos - shown only on mobile */}
          <div className="mobile-logos">
            <img src="/fit-logo.jpg" alt="F-IT Logo" className="mobile-logo" />
            <img src="/fyou-logo-edit.jpeg" alt="F-YOU Logo" className="mobile-logo" />
          </div>
          
          <div className="system-layout">
            <div className="desktop-logo">
              <img src="/fit-logo.jpg" alt="F-IT Logo" />
            </div>
            
            <div className="system-grid">
              <div className="system-card orange-card">
                <h3><span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score</h3>
                <p className="card-subtitle">Individual Wellbeing</p>
                <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> measures personal wellbeing - that critical moment when someone's mental health is so compromised they say "F**k it" and make a dangerous decision.</p>
                <p>It captures how individuals are feeling, their intent to engage, and their mental clarity in the moment.</p>
              </div>
              
              <div className="system-card blue-card">
                <h3><span className="fit-inline"><span className="f-blue">F</span>-YOU</span> Score</h3>
                <p className="card-subtitle">Company Culture</p>
                <p><span className="fit-inline"><span className="f-blue">F</span>-YOU</span> measures how the organisation responds - whether employees feel heard and supported, or dismissed and disengaged.</p>
                <p>When someone with a high <span className="fit-inline"><span className="f-orange">F</span>-IT</span> score encounters poor organisational response, it becomes a high <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> score - and that's where real problems&nbsp;begin.</p>
              </div>
            </div>
            
            <div className="desktop-logo">
              <img src="/fyou-logo-edit.jpeg" alt="F-YOU Logo" />
            </div>
          </div>
          
          <p className="system-summary">Together, <span className="fit-inline"><span className="f-orange">F</span>-IT</span> and <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> provide a complete picture of workplace wellbeing - addressing both individual mental health and organisational culture.</p>
        </div>
      </section>

      {/* What F-IT Captures */}
      <section className="methodology">
        <div className="container">
          <h2 className="section-title">What <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Captures</h2>
          <p className="section-subtitle"><span className="fit-inline"><span className="f-orange">F</span>-IT</span> measures three key dimensions that influence that critical "F**k it" moment</p>
          
          <div className="grid grid-3">
            <div className="method-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1701980032097-65daaf4b5882?w=800&q=80" alt="Feeling - Emotional Wellbeing" />
              </div>
              <h3>Feeling</h3>
              <p>How you're experiencing your emotional state right now - from thriving to&nbsp;struggling</p>
            </div>
            <div className="method-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1666718844263-7b8076df98a1?w=800&q=80" alt="Intent - Motivation" />
              </div>
              <h3>Intent</h3>
              <p>Your capacity and motivation to engage with work, relationships, and life&nbsp;activities</p>
            </div>
            <div className="method-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1765438858380-7cd73d0dca60?w=800&q=80" alt="Thoughts - Mental Clarity" />
              </div>
              <h3>Thoughts</h3>
              <p>Your mental clarity, focus, and overall cognitive wellbeing throughout your&nbsp;day</p>
            </div>
          </div>
        </div>
      </section>

      {/* What F-YOU Captures */}
      <section className="you-methodology">
        <div className="container">
          <h2 className="section-title">What <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> Captures</h2>
          <p className="section-subtitle"><span className="fit-inline"><span className="f-blue">F</span>-YOU</span> measures how the organisation responds to employee wellbeing - three key dimensions that determine whether people feel supported or dismissed</p>
          
          <div className="grid grid-3">
            <div className="method-card blue-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80" alt="Communication - Organisational Response" />
              </div>
              <h3>Communication</h3>
              <p>How effectively the organisation listens to and addresses employee concerns about wellbeing and&nbsp;safety</p>
            </div>
            <div className="method-card blue-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&q=80" alt="Support - Organisational Culture" />
              </div>
              <h3>Support</h3>
              <p>The practical resources, systems, and culture in place to help employees when they're&nbsp;struggling</p>
            </div>
            <div className="method-card blue-card">
              <div className="method-image">
                <img src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80" alt="Trust - Psychological Safety" />
              </div>
              <h3>Trust</h3>
              <p>Whether employees feel psychologically safe to speak up about wellbeing issues without fear of negative&nbsp;consequences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding the Scale */}
      <section className="the-scale">
        <div className="container">
          <h2 className="section-title">Understanding the <span className="fit-inline"><span className="f-orange">F</span>-IT</span> 0-10 Scale</h2>
          
          <div className="scale-intro">
            <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score applies to both <strong>safety and wellbeing</strong> because they are intrinsically linked. When someone's wellbeing deteriorates, their decision-making becomes compromised.</p>
            <p>This isn't just theory - it's based on Jason's lived experience and backed by Tim's decades of research into human error and organisational&nbsp;psychology.</p>
          </div>

          <div className="scale-image-container">
            <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/azu9h5da_new%20fit%20scale.jpg" alt="F-IT Score Scale 0-10" className="scale-image" />
          </div>

          <div className="scale-explanation">
            <div className="scale-segment green-segment">
              <h3>0-3: Excelling & Thriving</h3>
              <p><strong>Rules, Systems, Procedures</strong></p>
              <p>A person at the green end of the scale follows rules and procedures naturally. They're at peak performance with a positive mental state and high engagement. Safety protocols are followed instinctively, decisions are sound, and people actively contribute to a positive workplace culture.</p>
              <p>This is where organisations want their employees to be - mentally clear, emotionally balanced, and fully engaged with their work.</p>
            </div>
            <div className="scale-segment orange-segment">
              <h3>4-7: Distracted to Struggling</h3>
              <p><strong>Errors Become More Likely</strong></p>
              <p>As people move through the orange zone, they're more likely to make errors. They're managing but feeling stretched, with reduced capacity and signs of increasing stress. Mental clarity decreases, concentration wavers, and mistakes that wouldn't normally happen are more likely to occur.</p>
              <p>This is the warning zone - where wellbeing issues begin to impact performance and safety. Early intervention here can prevent progression into the red zone.</p>
            </div>
            <div className="scale-segment red-segment">
              <h3>8-10: In Crisis</h3>
              <p><strong>Violations More Likely to Occur</strong></p>
              <p>In the red zone, people experience significant distress and urgent need for support. This is where violations are more likely to happen - that's when "F**k it" moments can occur and accidents potentially happen. People may knowingly take risks they wouldn't normally take because their wellbeing is so compromised they've stopped caring about consequences.</p>
              <p><strong>This is where <span className="fit-inline"><span className="f-orange">F</span>-IT</span> can turn into <span className="fit-inline"><span className="f-blue">F</span>-YOU</span>.</strong> When someone in crisis reaches out for help and is dismissed or ignored by their organisation, their personal wellbeing crisis becomes organisational disengagement - and that's when the real damage occurs.</p>
              <p>This was Jason's reality in 1993, standing at the bottom of that untied ladder - compromised wellbeing led to a decision that changed his life forever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Transform Your Organisation?</h2>
          <p>See how F-IT Score can change the way you approach wellbeing and safety</p>
          <div className="cta-buttons">
            <Link to="/how-it-works" className="cta-btn cta-btn-primary">Explore Our Services</Link>
            <Link to="/contact" className="cta-btn cta-btn-secondary">Get In Touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WhyItWorks;
